import * as React from 'react';
import type { FieldValues, FieldPath, UseControllerProps, UseFormUnregister } from 'react-hook-form';

import { InputCell } from '../../RHFTableCellComponents/Input/InputCell';
import type { InputCellType, InputSharedProps } from '../../RHFTableCellComponents/Input/types';
import type { RenderMethodParams } from '../RHFFieldCellWrapper';
import RHFFieldCellWrapper from '../RHFFieldCellWrapper';

type Props<TFieldValues extends FieldValues = FieldValues, TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>, TTransformedValues = TFieldValues> =
	UseControllerProps<TFieldValues, TName, TTransformedValues> & {
		type: InputCellType;
		rhfId: number | string;
		label?: string;
		placeholder?: string;
		onChange?: (name: string) => void;
		onBlur: () => void;
		unregister: UseFormUnregister<TFieldValues>;
		fieldsLength: number;
		className?: string;
	} & InputSharedProps;

function InputFieldCell<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
	TTransformedValues extends FieldValues = TFieldValues>(props: Props<TFieldValues, TName, TTransformedValues>) {
	const { name, rhfId, placeholder, type, onChange, onBlur, prefixGlyphs, suffixGlyphs, ...rest } = props;

	const renderInputCell = React.useCallback((
		{
			field,
			error,
			onFieldChange,
			onFieldBlur,
		}: RenderMethodParams<TFieldValues, TName>
	) => (
		<InputCell
			elementKey={`${rhfId}`}
			error={error ?? undefined}
			key={name + rhfId}
			name={field.name}
			onBlur={onFieldBlur}
			onChange={onFieldChange}
			placeholder={placeholder}
			prefixGlyphs={prefixGlyphs}
			suffixGlyphs={suffixGlyphs}
			type={type}
			value={field.value}
		/>
	), [rhfId, name, placeholder, type, prefixGlyphs, suffixGlyphs]);

	return (
		<RHFFieldCellWrapper
			name={name}
			onBlur={onBlur}
			onChange={onChange}
			{...rest}
			render={renderInputCell}
		/>
	);
}

export default React.memo(InputFieldCell) as typeof InputFieldCell;
